import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { downloadTemplateDefinition, useTemplateMutations, useTemplateVersions, type GroupInput, type QuestionInput, type ScoringRules } from '../api/admin'
import { useTemplateVersion, useTemplates } from '../api/hooks'
import type { AnswerOption, FindingSeverity, HeaderField, HeaderFieldType, Question, QuestionGroup } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Modal, Tabs, fmtDateTime } from '../components/ui'
import { errMsg } from './TemplatesPage'

const HEADER_TYPES: HeaderFieldType[] = ['Text', 'MultilineText', 'Date', 'DateList', 'DateRange', 'Number', 'User', 'UserList', 'Company', 'Site', 'Select']
const SEVERITIES: FindingSeverity[] = ['Minor', 'Major', 'ZeroTolerance']

export default function TemplateBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const templates = useTemplates()
  const template = templates.data?.find((x) => x.id === id)
  const versions = useTemplateVersions(id)
  const draft = versions.data?.find((v) => v.status === 'Draft')
  const selectedId = params.get('v') ?? draft?.id ?? template?.currentVersionId ?? versions.data?.[0]?.id
  const version = useTemplateVersion(selectedId)
  const m = useTemplateMutations(id)
  const [tab, setTab] = useState('questions')
  const [error, setError] = useState<string | null>(null)
  const canManage = user?.roles.some((r) => r.role === 'TenantAdmin' || r.role === 'SuperAdmin') ?? false
  const editable = canManage && !template?.isGlobal && version.data?.status === 'Draft'
  const v = version.data

  const act = async (fn: () => Promise<unknown>, after?: () => void) => {
    setError(null)
    try {
      await fn()
      after?.()
    } catch (e) {
      setError(errMsg(e))
    }
  }

  if (!template && templates.isLoading) return <p>{t('common.loading')}</p>
  if (!template) return <p className="error">{t('templates.notFound')}</p>

  return (
    <div className="stack">
      <div className="row between wrap">
        <div>
          <div className="muted small"><Link to="/templates">{t('templates.title')}</Link> / {template.code}</div>
          <h2 style={{ margin: 0 }}>{template.name} <Badge>{t(`formKind.${template.kind}`)}</Badge> {template.isGlobal ? <Badge color="#4a3aa7">{t('templates.global')}</Badge> : null}</h2>
        </div>
        {canManage && !template.isGlobal ? (
          <div className="row wrap">
            {!draft ? <button className="secondary" onClick={() => void act(() => m.createDraft.mutateAsync(undefined), () => setParams({}))}>{t('builder.newDraft')}</button> : null}
            {v?.status === 'Draft' ? <button className="secondary danger" onClick={() => { if (confirm(t('builder.deleteDraftConfirm'))) void act(() => m.deleteDraft.mutateAsync(v.id), () => setParams({})) }}>{t('builder.deleteDraft')}</button> : null}
            {v?.status === 'Draft' ? <button onClick={() => { const note = prompt(t('builder.changeNotePrompt')) ?? undefined; void act(() => m.publish.mutateAsync({ versionId: v.id, changeNote: note })) }} disabled={m.publish.isPending}>{t('builder.publish')}</button> : null}
          </div>
        ) : null}
      </div>
      {template.isGlobal && canManage ? <p className="muted small">{t('builder.globalReadOnly')}</p> : null}
      {error ? <p className="error">{error}</p> : null}

      <div className="grid detail">
        <div className="stack">
          {v ? (
            <>
              <div className="row between wrap">
                <div className="row">
                  <b>v{v.versionNo}</b>
                  <Badge color={v.status === 'Published' ? '#008300' : v.status === 'Draft' ? '#eda100' : undefined}>{t(`versionStatus.${v.status}`)}</Badge>
                  {v.publishedAt ? <span className="muted small">{fmtDateTime(v.publishedAt)}</span> : null}
                </div>
                <button className="ghost" onClick={() => void downloadTemplateDefinition(v.id, `${template.code}-v${v.versionNo}.json`)}>{t('builder.exportJson')}</button>
              </div>
              <Tabs active={tab} onChange={setTab} tabs={[
                { key: 'questions', label: t('builder.questions'), badge: <span className="muted small">{v.questions.length}</span> },
                { key: 'groups', label: t('builder.groups'), badge: <span className="muted small">{v.groups.length}</span> },
                { key: 'options', label: t('builder.options') },
                { key: 'header', label: t('builder.headerFields') },
                { key: 'scoring', label: t('builder.scoring') },
                { key: 'general', label: t('builder.general') },
              ]} />
              {tab === 'questions' ? <QuestionsTab versionId={v.id} questions={v.questions} groups={v.groups} scored={template.kind === 'ScoredAudit'} editable={editable} m={m} onError={setError} /> : null}
              {tab === 'groups' ? <GroupsTab versionId={v.id} groups={v.groups} questions={v.questions} editable={editable} m={m} onError={setError} /> : null}
              {tab === 'options' ? <OptionsTab key={`${v.id}-${v.syncVersion}`} versionId={v.id} options={v.answerOptions} scored={template.kind === 'ScoredAudit'} editable={editable} m={m} onError={setError} /> : null}
              {tab === 'header' ? <HeaderTab key={`${v.id}-${v.syncVersion}`} versionId={v.id} fields={v.headerFields} editable={editable} m={m} onError={setError} /> : null}
              {tab === 'scoring' ? <ScoringTab key={`${v.id}-${v.syncVersion}`} versionId={v.id} rules={v.scoringRules as ScoringRules} settings={v.settings} editable={editable} m={m} onError={setError} /> : null}
              {tab === 'general' ? <GeneralTab key={template.syncVersion} template={template} editable={canManage && !template.isGlobal} m={m} onError={setError} /> : null}
            </>
          ) : <p>{t('common.loading')}</p>}
        </div>

        <div className="card stack">
          <h3>{t('builder.versions')}</h3>
          {versions.data?.map((x) => (
            <button key={x.id} className={`nav-item ${x.id === selectedId ? 'active' : ''}`} onClick={() => setParams({ v: x.id })}>
              <span>v{x.versionNo} · {t(`versionStatus.${x.status}`)}</span>
              <span className="muted small">{x.questionCount} {t('builder.q')} · {x.instanceCount} {t('builder.reports')}</span>
              {x.changeNote ? <span className="muted small" style={{ width: '100%' }}>{x.changeNote}</span> : null}
            </button>
          ))}
          <p className="muted small">{t('builder.versionHint')}</p>
        </div>
      </div>
    </div>
  )
}

type M = ReturnType<typeof useTemplateMutations>
interface TabProps { versionId: string; editable: boolean; m: M; onError: (s: string | null) => void }

function guard(fn: () => Promise<unknown>, onError: (s: string | null) => void, after?: () => void) {
  onError(null)
  fn().then(() => after?.()).catch((e: unknown) => onError(errMsg(e)))
}

// ---------------------------------------------------------------- questions

function QuestionsTab({ versionId, questions, groups, scored, editable, m, onError }: TabProps & { questions: Question[]; groups: QuestionGroup[]; scored: boolean }) {
  const { t } = useTranslation()
  const [edit, setEdit] = useState<QuestionInput | null>(null)
  const [bulk, setBulk] = useState<string | null>(null)
  const [bulkGroup, setBulkGroup] = useState('')
  const [filter, setFilter] = useState('')
  const groupName = (gid?: string) => { const g = groups.find((x) => x.id === gid); return g ? `${g.code} ${g.name}` : '' }
  const ordered = useMemo(() => [...questions].sort((a, b) => a.sortOrder - b.sortOrder), [questions])
  const visible = ordered.filter((q) => !filter || q.text.toLowerCase().includes(filter.toLowerCase()) || String(q.questionNo).includes(filter))
  const move = (q: Question, dir: -1 | 1) => {
    const idx = ordered.findIndex((x) => x.id === q.id)
    const j = idx + dir
    if (j < 0 || j >= ordered.length) return
    const ids = ordered.map((x) => x.id)
    ;[ids[idx], ids[j]] = [ids[j], ids[idx]]
    guard(() => m.reorderQuestions.mutateAsync({ versionId, ids }), onError)
  }
  const nextNo = (Math.max(0, ...questions.map((q) => q.questionNo)) || 0) + 1
  const blank = (): QuestionInput => ({ questionNo: nextNo, text: '', maxPoints: scored ? 10 : 0, requiresPhotoOnFinding: false, groupId: groups[0]?.id ?? null, defaultSeverity: scored ? 'Minor' : null })
  const parseBulk = (text: string): QuestionInput[] =>
    text.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
      const parts = line.split('\t').length > 1 ? line.split('\t') : line.split('|')
      const [a, b, c] = parts.map((p) => p.trim())
      const noFirst = /^\d+$/.test(a ?? '')
      return { questionNo: noFirst ? Number(a) : 0, text: noFirst ? (b ?? '') : (a ?? ''), maxPoints: Number((noFirst ? c : b) ?? (scored ? 10 : 0)) || 0, requiresPhotoOnFinding: false, groupId: bulkGroup || null, defaultSeverity: (scored ? 'Minor' : null) as FindingSeverity | null }
    }).filter((q) => q.text)

  return (
    <div className="card stack">
      <div className="row between wrap">
        <input placeholder={t('common.search')} value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 280 }} />
        {editable ? (
          <div className="row">
            <button className="secondary" onClick={() => { setBulkGroup(groups[0]?.id ?? ''); setBulk('') }}>{t('builder.bulkAdd')}</button>
            <button onClick={() => setEdit(blank())}>{t('builder.addQuestion')}</button>
          </div>
        ) : null}
      </div>
      <div className="table-wrap">
        <table className="table compact">
          <thead><tr><th>#</th><th>{t('builder.no')}</th><th>{t('builder.text')}</th><th>{t('builder.group')}</th>{scored ? <th className="num">{t('builder.points')}</th> : null}<th>{t('builder.severity')}</th><th>{t('builder.photo')}</th>{editable ? <th /> : null}</tr></thead>
          <tbody>
            {visible.map((q) => (
              <tr key={q.id}>
                <td className="muted small">{q.sortOrder}</td>
                <td><b>{q.questionNo}</b></td>
                <td>{q.text}{q.standardActionText ? <div className="muted small">↳ {q.standardActionText}</div> : null}{q.reference ? <div className="muted small">{q.reference}</div> : null}</td>
                <td className="small">{groupName(q.groupId)}</td>
                {scored ? <td className="num">{q.maxPoints}</td> : null}
                <td className="small">{q.defaultSeverity ? t(`severity.${q.defaultSeverity}`) : '–'}</td>
                <td className="small">{q.requiresPhotoOnFinding ? t('common.yes') : '–'}</td>
                {editable ? (
                  <td className="row" style={{ whiteSpace: 'nowrap' }}>
                    <button className="ghost" title={t('builder.up')} onClick={() => move(q, -1)}>↑</button>
                    <button className="ghost" title={t('builder.down')} onClick={() => move(q, 1)}>↓</button>
                    <button className="ghost" onClick={() => setEdit({ ...q, groupId: q.groupId ?? null, defaultSeverity: q.defaultSeverity ?? null })}>{t('builder.edit')}</button>
                    <button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => { if (confirm(t('common.deleteConfirm'))) guard(() => m.deleteQuestion.mutateAsync({ versionId, questionId: q.id }), onError) }}>{t('common.delete')}</button>
                  </td>
                ) : null}
              </tr>
            ))}
            {visible.length === 0 ? <tr><td colSpan={8} className="muted">{t('builder.noQuestions')}</td></tr> : null}
          </tbody>
        </table>
      </div>

      {edit ? (
        <Modal title={edit.id ? t('builder.editQuestion') : t('builder.addQuestion')} onClose={() => setEdit(null)} width={680}>
          <div className="grid two">
            <label>{t('builder.no')}<input type="number" value={edit.questionNo} onChange={(e) => setEdit({ ...edit, questionNo: Number(e.target.value) })} /></label>
            <label>{t('builder.group')}
              <select value={edit.groupId ?? ''} onChange={(e) => setEdit({ ...edit, groupId: e.target.value || null })}>
                <option value="">{t('common.none')}</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.parentGroupId ? '  ' : ''}{g.code} {g.name}</option>)}
              </select>
            </label>
          </div>
          <label>{t('builder.text')}<textarea rows={3} value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} /></label>
          <label>{t('builder.helpText')}<textarea rows={2} value={edit.helpText ?? ''} onChange={(e) => setEdit({ ...edit, helpText: e.target.value })} /></label>
          <label>{t('builder.standardAction')}<textarea rows={2} value={edit.standardActionText ?? ''} onChange={(e) => setEdit({ ...edit, standardActionText: e.target.value })} /></label>
          <div className="grid two">
            {scored ? <label>{t('builder.points')}<input type="number" step="0.5" value={edit.maxPoints} onChange={(e) => setEdit({ ...edit, maxPoints: Number(e.target.value) })} /></label> : null}
            <label>{t('builder.severity')}
              <select value={edit.defaultSeverity ?? ''} onChange={(e) => setEdit({ ...edit, defaultSeverity: (e.target.value || null) as FindingSeverity | null })}>
                <option value="">{t('common.none')}</option>
                {SEVERITIES.map((s) => <option key={s} value={s}>{t(`severity.${s}`)}</option>)}
              </select>
            </label>
            <label>{t('builder.reference')}<input value={edit.reference ?? ''} onChange={(e) => setEdit({ ...edit, reference: e.target.value })} placeholder="6331 md.15" /></label>
            <label className="row"><input type="checkbox" checked={edit.requiresPhotoOnFinding} onChange={(e) => setEdit({ ...edit, requiresPhotoOnFinding: e.target.checked })} /> {t('builder.photoRequired')}</label>
          </div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setEdit(null)}>{t('common.cancel')}</button>
            <button disabled={!edit.text.trim()} onClick={() => guard(() => m.upsertQuestion.mutateAsync({ versionId, input: { ...edit, helpText: edit.helpText || null, standardActionText: edit.standardActionText || null, reference: edit.reference || null } }), onError, () => setEdit(null))}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}

      {bulk !== null ? (
        <Modal title={t('builder.bulkAdd')} onClose={() => setBulk(null)} width={720}>
          <p className="muted small">{t('builder.bulkHint')}</p>
          <label>{t('builder.group')}
            <select value={bulkGroup} onChange={(e) => setBulkGroup(e.target.value)}>
              <option value="">{t('common.none')}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.code} {g.name}</option>)}
            </select>
          </label>
          <textarea rows={12} value={bulk} onChange={(e) => setBulk(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} placeholder={'1\tAcil çıkış kapıları açık mı?\t10\n2\tYangın tüpleri kontrol edilmiş mi?\t5'} />
          <div className="muted small">{t('builder.bulkPreview', { count: parseBulk(bulk).length })}</div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setBulk(null)}>{t('common.cancel')}</button>
            <button disabled={parseBulk(bulk).length === 0} onClick={() => guard(() => m.bulkAdd.mutateAsync({ versionId, questions: parseBulk(bulk) }), onError, () => setBulk(null))}>{t('builder.addCount', { count: parseBulk(bulk).length })}</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------- groups

function GroupsTab({ versionId, groups, questions, editable, m, onError }: TabProps & { groups: QuestionGroup[]; questions: Question[] }) {
  const { t } = useTranslation()
  const [edit, setEdit] = useState<GroupInput | null>(null)
  const mains = groups.filter((g) => !g.parentGroupId).sort((a, b) => a.sortOrder - b.sortOrder)
  const children = (pid: string) => groups.filter((g) => g.parentGroupId === pid).sort((a, b) => a.sortOrder - b.sortOrder)
  const count = (gid: string) => questions.filter((q) => q.groupId === gid).length
  const move = (list: QuestionGroup[], g: QuestionGroup, dir: -1 | 1) => {
    const idx = list.findIndex((x) => x.id === g.id)
    const j = idx + dir
    if (j < 0 || j >= list.length) return
    const ids = list.map((x) => x.id)
    ;[ids[idx], ids[j]] = [ids[j], ids[idx]]
    guard(() => m.reorderGroups.mutateAsync({ versionId, ids }), onError)
  }
  const row = (g: QuestionGroup, list: QuestionGroup[], sub: boolean) => (
    <tr key={g.id}>
      <td style={{ paddingLeft: sub ? 28 : undefined }}><b>{g.code}</b></td>
      <td>{g.name}</td>
      <td className="small">{g.letterCode ?? '–'}</td>
      <td className="num">{g.number ?? '–'}</td>
      <td className="num">{sub ? '' : g.weight}</td>
      <td className="num">{count(g.id)}</td>
      {editable ? (
        <td className="row" style={{ whiteSpace: 'nowrap' }}>
          <button className="ghost" onClick={() => move(list, g, -1)}>↑</button>
          <button className="ghost" onClick={() => move(list, g, 1)}>↓</button>
          {!sub ? <button className="ghost" onClick={() => setEdit({ parentGroupId: g.id, code: `${g.letterCode ?? g.code}${g.number ?? ''}.${children(g.id).length + 1}`, name: '', letterCode: g.letterCode, number: g.number })}>{t('builder.addSubGroup')}</button> : null}
          <button className="ghost" onClick={() => setEdit({ id: g.id, parentGroupId: g.parentGroupId ?? null, code: g.code, name: g.name, letterCode: g.letterCode ?? '', number: g.number ?? null, weight: g.weight })}>{t('builder.edit')}</button>
          <button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => { if (confirm(t('builder.deleteGroupConfirm'))) guard(() => m.deleteGroup.mutateAsync({ versionId, groupId: g.id }), onError) }}>{t('common.delete')}</button>
        </td>
      ) : null}
    </tr>
  )
  return (
    <div className="card stack">
      <div className="row between wrap">
        <p className="muted small" style={{ margin: 0 }}>{t('builder.groupsHint')}</p>
        {editable ? <button onClick={() => setEdit({ code: String(mains.length + 1), name: '', letterCode: String.fromCharCode(65 + mains.length), number: mains.length + 1, weight: 1 })}>{t('builder.addGroup')}</button> : null}
      </div>
      <div className="table-wrap">
        <table className="table compact">
          <thead><tr><th>{t('builder.code')}</th><th>{t('builder.name')}</th><th>{t('builder.letter')}</th><th className="num">{t('builder.number')}</th><th className="num">{t('builder.weight')}</th><th className="num">{t('builder.questions')}</th>{editable ? <th /> : null}</tr></thead>
          <tbody>
            {mains.map((g) => [row(g, mains, false), ...children(g.id).map((c) => row(c, children(g.id), true))])}
            {mains.length === 0 ? <tr><td colSpan={7} className="muted">{t('builder.noGroups')}</td></tr> : null}
          </tbody>
        </table>
      </div>
      {edit ? (
        <Modal title={edit.id ? t('builder.editGroup') : edit.parentGroupId ? t('builder.addSubGroup') : t('builder.addGroup')} onClose={() => setEdit(null)}>
          <label>{t('builder.parent')}
            <select value={edit.parentGroupId ?? ''} onChange={(e) => setEdit({ ...edit, parentGroupId: e.target.value || null })}>
              <option value="">{t('builder.mainGroup')}</option>
              {mains.filter((g) => g.id !== edit.id).map((g) => <option key={g.id} value={g.id}>{g.code} {g.name}</option>)}
            </select>
          </label>
          <div className="grid two">
            <label>{t('builder.code')}<input value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} /></label>
            <label>{t('builder.letter')}<input value={edit.letterCode ?? ''} onChange={(e) => setEdit({ ...edit, letterCode: e.target.value })} maxLength={3} /></label>
            <label>{t('builder.number')}<input type="number" value={edit.number ?? ''} onChange={(e) => setEdit({ ...edit, number: e.target.value ? Number(e.target.value) : null })} /></label>
            {!edit.parentGroupId ? <label>{t('builder.weight')}<input type="number" step="0.1" value={edit.weight ?? 1} onChange={(e) => setEdit({ ...edit, weight: Number(e.target.value) })} /></label> : null}
          </div>
          <label>{t('builder.name')}<input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></label>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setEdit(null)}>{t('common.cancel')}</button>
            <button disabled={!edit.code.trim() || !edit.name.trim()} onClick={() => guard(() => m.upsertGroup.mutateAsync({ versionId, input: { ...edit, letterCode: edit.letterCode || null } }), onError, () => setEdit(null))}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------- answer options

function OptionsTab({ versionId, options, scored, editable, m, onError }: TabProps & { options: AnswerOption[]; scored: boolean }) {
  const { t } = useTranslation()
  const [rows, setRows] = useState<AnswerOption[]>(options)
  const set = (i: number, patch: Partial<AnswerOption>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const dirty = JSON.stringify(rows) !== JSON.stringify(options)
  return (
    <div className="card stack">
      <p className="muted small">{t('builder.optionsHint')}</p>
      <div className="table-wrap">
        <table className="table compact">
          <thead><tr><th>{t('builder.code')}</th><th>{t('builder.label')}</th><th>{t('builder.exportLabel')}</th>{scored ? <th>{t('builder.multiplier')}</th> : null}<th>{t('builder.createsFinding')}</th><th>{t('builder.color')}</th>{editable ? <th /> : null}</tr></thead>
          <tbody>
            {rows.map((o, i) => (
              <tr key={i}>
                <td><input value={o.code} disabled={!editable} onChange={(e) => set(i, { code: e.target.value.toUpperCase() })} style={{ width: 150 }} /></td>
                <td><input value={o.label} disabled={!editable} onChange={(e) => set(i, { label: e.target.value })} /></td>
                <td><input value={o.exportLabel ?? ''} disabled={!editable} onChange={(e) => set(i, { exportLabel: e.target.value })} /></td>
                {scored ? <td><input type="number" step="0.05" min={0} max={1} value={o.scoreMultiplier ?? ''} placeholder="N/A" disabled={!editable} onChange={(e) => set(i, { scoreMultiplier: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: 90 }} /></td> : null}
                <td><input type="checkbox" checked={o.createsFinding} disabled={!editable} onChange={(e) => set(i, { createsFinding: e.target.checked })} /></td>
                <td><input type="color" value={o.color ?? '#757575'} disabled={!editable} onChange={(e) => set(i, { color: e.target.value })} style={{ width: 48, padding: 2 }} /></td>
                {editable ? <td><button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => setRows(rows.filter((_, j) => j !== i))}>✕</button></td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable ? (
        <div className="row between wrap">
          <button className="secondary" onClick={() => setRows([...rows, { code: '', label: '', exportLabel: '', scoreMultiplier: scored ? 0 : null, createsFinding: false, color: '#757575', sortOrder: rows.length + 1 }])}>{t('builder.addOption')}</button>
          <button disabled={!dirty} onClick={() => guard(() => m.setOptions.mutateAsync({ versionId, options: rows.map((r, i) => ({ ...r, sortOrder: i + 1 })) }), onError)}>{t('common.save')}</button>
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------- header fields

function HeaderTab({ versionId, fields, editable, m, onError }: TabProps & { fields: HeaderField[] }) {
  const { t } = useTranslation()
  const [rows, setRows] = useState<HeaderField[]>(fields)
  const set = (i: number, patch: Partial<HeaderField>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const dirty = JSON.stringify(rows) !== JSON.stringify(fields)
  const move = (i: number, dir: -1 | 1) => { const j = i + dir; if (j < 0 || j >= rows.length) return; const n = [...rows]; [n[i], n[j]] = [n[j], n[i]]; setRows(n) }
  return (
    <div className="card stack">
      <p className="muted small">{t('builder.headerHint')}</p>
      <div className="table-wrap">
        <table className="table compact">
          <thead><tr><th>{t('builder.key')}</th><th>{t('builder.label')}</th><th>{t('builder.type')}</th><th>{t('builder.required')}</th><th>{t('builder.auditDate')}</th><th>{t('builder.auditorList')}</th><th>{t('builder.instanceCode')}</th><th>{t('builder.readOnly')}</th>{editable ? <th /> : null}</tr></thead>
          <tbody>
            {rows.map((f, i) => (
              <tr key={i}>
                <td><input value={f.key} disabled={!editable} onChange={(e) => set(i, { key: e.target.value })} style={{ width: 150 }} /></td>
                <td><input value={f.label} disabled={!editable} onChange={(e) => set(i, { label: e.target.value })} /></td>
                <td><select value={f.type} disabled={!editable} onChange={(e) => set(i, { type: e.target.value as HeaderFieldType })}>{HEADER_TYPES.map((x) => <option key={x} value={x}>{t(`headerFieldType.${x}`)}</option>)}</select></td>
                <td><input type="checkbox" checked={f.required} disabled={!editable} onChange={(e) => set(i, { required: e.target.checked })} /></td>
                <td><input type="checkbox" checked={f.isAuditDate} disabled={!editable} onChange={(e) => set(i, { isAuditDate: e.target.checked })} /></td>
                <td><input type="checkbox" checked={f.isAuditorList} disabled={!editable} onChange={(e) => set(i, { isAuditorList: e.target.checked })} /></td>
                <td><input type="checkbox" checked={f.isInstanceCode} disabled={!editable} onChange={(e) => set(i, { isInstanceCode: e.target.checked })} /></td>
                <td><input type="checkbox" checked={f.readOnly} disabled={!editable} onChange={(e) => set(i, { readOnly: e.target.checked })} /></td>
                {editable ? <td style={{ whiteSpace: 'nowrap' }}><button className="ghost" onClick={() => move(i, -1)}>↑</button><button className="ghost" onClick={() => move(i, 1)}>↓</button><button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => setRows(rows.filter((_, j) => j !== i))}>✕</button></td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable ? (
        <div className="row between wrap">
          <button className="secondary" onClick={() => setRows([...rows, { key: `field${rows.length + 1}`, label: '', type: 'Text', required: false, isAuditDate: false, isAuditorList: false, isInstanceCode: false, readOnly: false, sortOrder: rows.length + 1 }])}>{t('builder.addField')}</button>
          <button disabled={!dirty} onClick={() => guard(() => m.updateVersion.mutateAsync({ versionId, headerFields: rows.map((r, i) => ({ ...r, sortOrder: i + 1 })) }), onError)}>{t('common.save')}</button>
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------- scoring rules + settings

function ScoringTab({ versionId, rules, settings, editable, m, onError }: TabProps & { rules: ScoringRules; settings: Record<string, unknown> }) {
  const { t } = useTranslation()
  const [r, setR] = useState<ScoringRules>(rules)
  const [settingsText, setSettingsText] = useState(JSON.stringify(settings, null, 2))
  const setBand = (i: number, patch: Partial<ScoringRules['letterBands'][number]>) => setR({ ...r, letterBands: r.letterBands.map((b, j) => (j === i ? { ...b, ...patch } : b)) })
  const setCap = (i: number, patch: Partial<ScoringRules['majorCaps'][number]>) => setR({ ...r, majorCaps: r.majorCaps.map((b, j) => (j === i ? { ...b, ...patch } : b)) })
  const save = () => {
    let parsed: Record<string, unknown>
    try { parsed = JSON.parse(settingsText) as Record<string, unknown> } catch { onError(t('builder.invalidJson')); return }
    guard(() => m.updateVersion.mutateAsync({ versionId, scoringRules: r, settings: parsed }), onError)
  }
  return (
    <div className="grid two">
      <div className="card stack">
        <h3>{t('builder.letterBands')}</h3>
        <p className="muted small">{t('builder.letterBandsHint')}</p>
        <table className="table compact">
          <thead><tr><th>{t('builder.grade')}</th><th>{t('builder.minPercent')}</th>{editable ? <th /> : null}</tr></thead>
          <tbody>
            {r.letterBands.map((b, i) => (
              <tr key={i}>
                <td><input value={b.grade} disabled={!editable} onChange={(e) => setBand(i, { grade: e.target.value.toUpperCase() })} style={{ width: 70 }} /></td>
                <td><input type="number" value={b.minPercent} disabled={!editable} onChange={(e) => setBand(i, { minPercent: Number(e.target.value) })} style={{ width: 100 }} /></td>
                {editable ? <td><button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => setR({ ...r, letterBands: r.letterBands.filter((_, j) => j !== i) })}>✕</button></td> : null}
              </tr>
            ))}
          </tbody>
        </table>
        {editable ? <button className="secondary small-btn" onClick={() => setR({ ...r, letterBands: [...r.letterBands, { grade: '', minPercent: 0 }] })}>{t('builder.addBand')}</button> : null}

        <h3>{t('builder.majorCaps')}</h3>
        <p className="muted small">{t('builder.majorCapsHint')}</p>
        <table className="table compact">
          <thead><tr><th>{t('builder.maxMajors')}</th><th>{t('builder.maxGrade')}</th>{editable ? <th /> : null}</tr></thead>
          <tbody>
            {r.majorCaps.map((b, i) => (
              <tr key={i}>
                <td><input type="number" value={b.maxMajors} disabled={!editable} onChange={(e) => setCap(i, { maxMajors: Number(e.target.value) })} style={{ width: 90 }} /></td>
                <td><input value={b.maxGrade} disabled={!editable} onChange={(e) => setCap(i, { maxGrade: e.target.value.toUpperCase() })} style={{ width: 70 }} /></td>
                {editable ? <td><button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => setR({ ...r, majorCaps: r.majorCaps.filter((_, j) => j !== i) })}>✕</button></td> : null}
              </tr>
            ))}
          </tbody>
        </table>
        {editable ? <button className="secondary small-btn" onClick={() => setR({ ...r, majorCaps: [...r.majorCaps, { maxMajors: 0, maxGrade: 'A' }] })}>{t('builder.addCap')}</button> : null}
      </div>
      <div className="card stack">
        <h3>{t('builder.scoringOptions')}</h3>
        <label>{t('builder.groupMajorCapMode')}
          <select value={r.groupMajorCapMode} disabled={!editable} onChange={(e) => setR({ ...r, groupMajorCapMode: e.target.value as ScoringRules['groupMajorCapMode'] })}>
            {['None', 'Table', 'OneStepDown'].map((x) => <option key={x} value={x}>{t(`majorCapMode.${x}`)}</option>)}
          </select>
        </label>
        <label>{t('builder.overallMajorCapMode')}
          <select value={r.overallMajorCapMode} disabled={!editable} onChange={(e) => setR({ ...r, overallMajorCapMode: e.target.value as ScoringRules['overallMajorCapMode'] })}>
            {['None', 'Table', 'OneStepDown'].map((x) => <option key={x} value={x}>{t(`majorCapMode.${x}`)}</option>)}
          </select>
        </label>
        <label>{t('builder.overallMethod')}
          <select value={r.overallMethod} disabled={!editable} onChange={(e) => setR({ ...r, overallMethod: e.target.value as ScoringRules['overallMethod'] })}>
            <option value="PointSum">{t('builder.pointSum')}</option>
            <option value="GroupAverage">{t('builder.groupAverage')}</option>
          </select>
        </label>
        <label className="row"><input type="checkbox" checked={r.zeroToleranceForcesFail} disabled={!editable} onChange={(e) => setR({ ...r, zeroToleranceForcesFail: e.target.checked })} /> {t('builder.ztFail')}</label>
        <label className="row"><input type="checkbox" checked={r.zeroToleranceZeroesPercent} disabled={!editable} onChange={(e) => setR({ ...r, zeroToleranceZeroesPercent: e.target.checked })} /> {t('builder.ztZero')}</label>
        <div className="grid two">
          <label>{t('builder.ztGrade')}<input value={r.zeroToleranceGrade} disabled={!editable} onChange={(e) => setR({ ...r, zeroToleranceGrade: e.target.value.toUpperCase() })} /></label>
          <label>{t('builder.decimals')}<input type="number" min={0} max={2} value={r.percentDecimals} disabled={!editable} onChange={(e) => setR({ ...r, percentDecimals: Number(e.target.value) })} /></label>
        </div>
        <label>{t('builder.letterToGroup')}<input value={Object.entries(r.letterToGroup).map(([k, v]) => `${k}=${v}`).join(', ')} disabled={!editable} onChange={(e) => {
          const map: Record<string, number> = {}
          e.target.value.split(',').map((s) => s.trim()).filter(Boolean).forEach((pair) => { const [k, v] = pair.split('='); if (k && v && !Number.isNaN(Number(v))) map[k.trim()] = Number(v) })
          setR({ ...r, letterToGroup: map })
        }} placeholder="A=1, B=2, …" /></label>
        <label>{t('builder.settingsJson')}<textarea rows={6} value={settingsText} disabled={!editable} onChange={(e) => setSettingsText(e.target.value)} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} /></label>
        {editable ? <div className="row" style={{ justifyContent: 'flex-end' }}><button onClick={save}>{t('common.save')}</button></div> : null}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- general

function GeneralTab({ template, editable, m, onError }: { template: { id: string; code: string; name: string; description?: string; instanceCodePrefix?: string; isGlobal: boolean }; editable: boolean; m: M; onError: (s: string | null) => void }) {
  const { t } = useTranslation()
  const templates = useTemplates()
  const full = templates.data?.find((x) => x.id === template.id)
  const [name, setName] = useState(template.name)
  const [description, setDescription] = useState(template.description ?? '')
  const [prefix, setPrefix] = useState(template.instanceCodePrefix ?? '')
  const [active, setActive] = useState(true)
  return (
    <div className="card stack" style={{ maxWidth: 640 }}>
      <label>{t('templates.code')}<input value={template.code} disabled /></label>
      <label>{t('templates.name')}<input value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} /></label>
      <label>{t('templates.description')}<textarea rows={2} value={description} disabled={!editable} onChange={(e) => setDescription(e.target.value)} /></label>
      <label>{t('templates.prefix')}<input value={prefix} disabled={!editable} onChange={(e) => setPrefix(e.target.value)} /></label>
      <p className="muted small">{t('templates.prefixHint')}</p>
      {editable ? <label className="row"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('templates.active')}</label> : null}
      {editable ? (
        <div className="row between">
          <button className="ghost" style={{ color: 'var(--danger)' }} onClick={() => { if (confirm(t('templates.deleteConfirm'))) guard(() => m.remove.mutateAsync(template.id), onError, () => { window.location.href = '/templates' }) }}>{t('common.delete')}</button>
          <button onClick={() => guard(() => m.update.mutateAsync({ name, description: description || undefined, instanceCodePrefix: prefix || undefined, isActive: active }), onError)}>{t('common.save')}</button>
        </div>
      ) : null}
      {full?.syncVersion ? null : null}
    </div>
  )
}
